import { useEffect, useRef, type ReactNode } from "react";
import { boldRuns, formatPolicyDate, policyBlocks } from "../../legal/parsePrivacy";
import { PRIVACY_BODY, PRIVACY_EFFECTIVE, PRIVACY_IS_DRAFT, PRIVACY_VERSION } from "../../legal/privacy";

/**
 * The privacy policy, read in the app.
 *
 * Built on the same `rules-*` sheet as What's New, for the reason that sheet
 * gives: two sheets meant to look identical and styled separately do not stay
 * identical. And it closes the same way — a tap anywhere outside, Escape, or
 * the buttons — because one of its doors is a small link in the corner a thumb
 * rests on, so an accidental open has to cost nothing.
 *
 * The text is privacy.md drawn through a few-line renderer (parsePrivacy.ts's
 * policyBlocks) rather than a markdown library: headings, paragraphs, bullets
 * and bold are all the policy uses, and the static `/privacy.html` is drawn
 * from the same blocks, so the two read the same.
 */
export function PrivacyModal({ onClose }: { onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const blocks = policyBlocks(PRIVACY_BODY);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Keep the page behind the sheet from scrolling along with it.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="rules-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="rules-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rules-head">
          <div>
            <div className="eyebrow">
              Version {PRIVACY_VERSION}
              {PRIVACY_EFFECTIVE && <> &middot; Effective {formatPolicyDate(PRIVACY_EFFECTIVE)}</>}
            </div>
            <h2 id="privacy-title" className="title rules-title">
              Privacy Policy
            </h2>
          </div>
          <button className="hud-exit rules-close" aria-label="Close privacy policy" onClick={onClose}>
            &#10005;
          </button>
        </div>

        {/* Outside the scrolling body, so it is still on screen halfway down:
            a draft is only ever shown in dev and local builds, and whoever is
            reading it there must not mistake it for what is in force. */}
        {PRIVACY_IS_DRAFT && (
          <div className="privacy-draft" role="note">
            Draft &mdash; not yet in effect
          </div>
        )}

        <div className="rules-body privacy-body">
          {blocks.map((block, i) => {
            switch (block.kind) {
              case "heading":
                return (
                  <h3 key={i} className="privacy-heading">
                    {emphasise(block.text)}
                  </h3>
                );
              case "paragraph":
                return (
                  <p key={i} className="privacy-p">
                    {emphasise(block.text)}
                  </p>
                );
              case "list":
                return (
                  <ul key={i} className="rules-list privacy-list">
                    {block.items.map((item, j) => (
                      <li key={j}>{emphasise(item)}</li>
                    ))}
                  </ul>
                );
            }
          })}
        </div>

        <div className="rules-foot">
          <button className="primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function emphasise(text: string): ReactNode[] {
  return boldRuns(text).map((run, i) => (run.bold ? <strong key={i}>{run.text}</strong> : <span key={i}>{run.text}</span>));
}
